import React, { useState, useMemo } from 'react'
import type { Collection } from './types'
import CollectionItem from './CollectionItem'
import CollectionCreateModal from './CollectionCreateModal'
import DeleteConfirmModal from './DeleteConfirmModal'

type CollectionsSidebarProps = {
  collections: Collection[]
  loading: boolean
  selectedId: number | 'orphan' | null
  onSelect: (id: number | 'orphan' | null) => void
  totalContents: number
  orphanCount: number
  contentCountByCollection: Record<number, number>
  onCreate: (title: string) => Promise<boolean>
  onRename: (id: number, title: string) => Promise<boolean>
  onDelete: (id: number) => Promise<boolean>
}

/**
 * Sidebar des collections avec liste, création et actions
 * Responsabilité unique : navigation et gestion des collections
 */
export default function CollectionsSidebar({
  collections,
  loading,
  selectedId,
  onSelect,
  totalContents,
  orphanCount,
  contentCountByCollection,
  onCreate,
  onRename,
  onDelete,
}: CollectionsSidebarProps) {
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Collection | null>(null)

  // Trier les collections : WordPress en premier, puis manuelles
  const sortedCollections = useMemo(() => {
    return [...collections].sort((a, b) => {
      // WordPress avant manuel
      if (a.source !== b.source) {
        return a.source === 'wordpress' ? -1 : 1
      }
      // Puis par titre
      return a.title.localeCompare(b.title, 'fr')
    })
  }, [collections])

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return false
    const success = await onDelete(deleteTarget.id)
    if (success) {
      // Si on supprime la collection sélectionnée, désélectionner
      if (selectedId === deleteTarget.id) {
        onSelect(null)
      }
    }
    return success
  }

  return (
    <>
      <aside className="w-72 shrink-0">
        <div className="sticky top-6 space-y-4 rounded-3xl border border-emerald-100/70 bg-white/90 p-4 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300">
              Collections ({collections.length})
            </h2>
            <button
              type="button"
              onClick={() => setCreateModalOpen(true)}
              className="rounded-full border border-emerald-200/70 p-1.5 text-emerald-600 transition hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/40"
              title="Nouvelle collection"
              data-onboarding="create-collection-btn"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Liste */}
          <div className="space-y-1">
            {/* Tous les contenus */}
            <button
              type="button"
              onClick={() => onSelect(null)}
              className={`w-full flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${
                selectedId === null
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                  : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800/60'
              }`}
            >
              <span className="font-medium">Tous les contenus</span>
              <span className="rounded-full bg-zinc-200/80 px-2 py-0.5 text-xs font-semibold text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300">
                {totalContents}
              </span>
            </button>

            {/* Collections */}
            {loading ? (
              <div className="animate-pulse space-y-2 py-2">
                <div className="h-8 rounded-xl bg-emerald-100/60 dark:bg-emerald-900/40" />
                <div className="h-8 rounded-xl bg-emerald-100/50 dark:bg-emerald-900/30" />
                <div className="h-8 rounded-xl bg-emerald-100/40 dark:bg-emerald-900/20" />
              </div>
            ) : (
              sortedCollections.map(collection => (
                <CollectionItem
                  key={collection.id}
                  collection={collection}
                  contentCount={contentCountByCollection[collection.id] || 0}
                  isSelected={selectedId === collection.id}
                  onSelect={() => onSelect(collection.id)}
                  onRename={(newTitle) => onRename(collection.id, newTitle)}
                  onDelete={() => setDeleteTarget(collection)}
                  canEdit={collection.source === 'manual'}
                />
              ))
            )}

            {/* Séparateur */}
            {(collections.length > 0 || orphanCount > 0) && (
              <div className="my-2 border-t border-zinc-200/70 dark:border-zinc-700/70" />
            )}

            {/* Orphelins */}
            <button
              type="button"
              onClick={() => onSelect('orphan')}
              className={`w-full flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${
                selectedId === 'orphan'
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                  : 'text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800/60'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-500" />
                <span className="font-medium">Sans collection</span>
              </div>
              <span className="rounded-full bg-amber-200/80 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/60 dark:text-amber-200">
                {orphanCount}
              </span>
            </button>
          </div>

          {/* Légende */}
          <div className="border-t border-zinc-200/50 pt-3 dark:border-zinc-700/50">
            <div className="flex items-center gap-4 text-xs text-zinc-500 dark:text-zinc-400">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span>Manuel</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-sky-500" />
                <span>WordPress</span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Modales */}
      <CollectionCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={onCreate}
      />

      <DeleteConfirmModal
        open={deleteTarget !== null}
        title="Supprimer la collection"
        itemName={deleteTarget?.title || ''}
        itemId={deleteTarget?.id || 0}
        warningMessage="Les contenus associés ne seront pas supprimés mais deviendront orphelins."
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}
